import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { templatesApi } from "@/services/templatesApi";
import { promptLibraryApi } from "@/services/promptLibraryApi";
import { projectsApi } from "@/services/projectsApi";
import { Template } from "@/types/models";

export function useActiveProjectTemplates(projectId: string, formatType?: string) {
  return useQuery({
    queryKey: ["projects", projectId, "templates", "active", formatType],
    queryFn: () => projectsApi.getActiveProjectTemplates(projectId, formatType),
    enabled: !!projectId,
  });
}

export function useAllActiveProjectTemplatesWithPrompts(projectId: string | undefined, formatType?: string) {
  return useQuery({
    queryKey: ["projects", projectId, "templates", "active-with-prompts", formatType ?? null],
    queryFn: () => projectsApi.getActiveProjectTemplatesWithPrompts(projectId!, formatType),
    enabled: !!projectId,
  });
}

// Read hook (for selectors and admin list)
export function useTemplates() {
  return useQuery({
    queryKey: ["templates"],
    queryFn: templatesApi.getTemplates,
  });
}

// Admin mutations
export function useTemplateAdmin() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["templates"] });

  const create = useMutation({
    mutationFn: templatesApi.createTemplate,
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Template> }) =>
      templatesApi.updateTemplate(id, data),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: templatesApi.deleteTemplate,
    onSuccess: invalidate,
  });

  return { create, update, remove };
}

export function useSaveTemplate() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      name,
      description,
      fields,
      template_type_id,
      template_formatetype_ids,
    }: {
      name: string;
      description?: string;
      fields: Array<{ id?: string; label: string; question: string }>;
      template_type_id?: number | null;
      template_formatetype_ids?: number[] | null;
    }) => {
      const templateId = crypto.randomUUID();

      await templatesApi.createTemplate({ id: templateId, name, description, template_type_id, template_formatetype_ids });

      await Promise.all(
        fields.map(async (field, index) => {
          if (field.id) {
            await promptLibraryApi.createAssociation({ id: crypto.randomUUID(), template_id: templateId, prompt_library_id: field.id, ordinal: index });
          } else {
            const plId = crypto.randomUUID();
            await promptLibraryApi.createPromptLibrary({ id: plId, name: field.label, priming: field.question });
            await promptLibraryApi.createAssociation({ id: crypto.randomUUID(), template_id: templateId, prompt_library_id: plId, ordinal: index });
          }
        })
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["templates"] });
      qc.invalidateQueries({ queryKey: ["prompt-libraries"] });
    },
  });
}
